import React, { useState } from "react";
import {
  Paper,
  Title,
  Button,
  Group,
  Stack,
  Loader,
  Alert,
  Modal,
  Text,
  TextInput,
} from "@mantine/core";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { IconPlus, IconAlertCircle, IconSearch } from "@tabler/icons-react";
import {
  getEmailLists,
  deleteEmailList,
} from "../../../../../api/outreach/email";
import { EmailOutreachQueryNames } from "../../../../../api/requests_responses/outreach/email";
import EmailListCard from "./EmailListCard";

const EmailLists: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [listToDelete, setListToDelete] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const {
    data: listsResponse,
    isLoading,
    error,
  } = useQuery(EmailOutreachQueryNames.GET_EMAIL_LISTS, getEmailLists, {
    refetchOnWindowFocus: false,
  });

  const deleteMutation = useMutation(
    (listId: number) => deleteEmailList(listId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(EmailOutreachQueryNames.GET_EMAIL_LISTS);
        setDeleteModalOpen(false);
        setListToDelete(null);
      },
    },
  );

  const handleCreateClick = () => {
    navigate("/outreach/email/lists/create");
  };

  const handleViewClick = (listId: number) => {
    navigate(`/outreach/email/lists/${listId}`);
  };

  const handleDeleteClick = (listId: number) => {
    setListToDelete(listId);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (listToDelete) {
      deleteMutation.mutate(listToDelete);
    }
  };

  if (isLoading) {
    return (
      <Paper p="md" shadow="sm">
        <Loader />
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        Failed to load email lists. Please try again.
      </Alert>
    );
  }

  const lists = listsResponse?.data?.email_lists || [];
  const filteredLists = lists.filter(
    (list) =>
      list.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      list.description?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={3}>Email Lists</Title>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreateClick}
          >
            Create New List
          </Button>
        </Group>

        {lists.length > 0 && (
          <TextInput
            placeholder="Search lists..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        )}

        {filteredLists.length === 0 && lists.length === 0 ? (
          <Paper p="xl" shadow="sm" withBorder>
            <Stack align="center" gap="md">
              <Text size="lg" c="dimmed" ta="center">
                No email lists yet
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                Create your first email list to start organizing contacts
              </Text>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={handleCreateClick}
              >
                Create Your First List
              </Button>
            </Stack>
          </Paper>
        ) : filteredLists.length === 0 ? (
          <Paper p="xl" shadow="sm" withBorder>
            <Text ta="center" c="dimmed">
              No lists found matching your search
            </Text>
          </Paper>
        ) : (
          <Group>
            {filteredLists.map((list) => (
              <EmailListCard
                key={list.id}
                list={list}
                onView={handleViewClick}
                onDelete={handleDeleteClick}
              />
            ))}
          </Group>
        )}
      </Stack>

      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Email List"
      >
        <Text mb="md">
          Are you sure you want to delete this email list? This action cannot be
          undone and all contacts in this list will be removed.
        </Text>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={() => setDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleDeleteConfirm}
            loading={deleteMutation.isLoading}
          >
            Delete
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default EmailLists;
